import {
    GridViewOutlined,
    KeyboardArrowDownOutlined,
    KeyboardArrowUpOutlined,
    ListOutlined,
} from "@mui/icons-material";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import { PieChart } from "@mui/x-charts";
import { generateClient } from "aws-amplify/api";
import { useEffect, useMemo, useState } from "react";
import { esQuery } from "src/graphql/queries";

const client = generateClient();

const DISPOSITION_COLORS = {
    Connected: "#00b74a",
    "No Answer": "#ff9800",
    Busy: "#f44336",
    Failed: "#9e9e9e",
    Voicemail: "#2196f3",
};

const CONNECTED_DISPOSITIONS = new Set([
    "Positive w/Amount",
    "Positive no Amount",
]);

const CONTACT_DISPOSITIONS = new Set([
    "Positive w/Amount",
    "Positive no Amount",
    "Negative",
    "Negative — DNC — Skill",
    "Negative — DNC — BU",
    "Other",
    "Called Party Hang Up",
]);

const BAD_NUMBER_DISPOSITIONS = new Set([
    "Invalid Number",
    "Disconnect",
    "Number Changed",
    "Fax Machine",
]);

const RETRY_DISPOSITIONS = new Set([
    "Retry - Answering Machine",
    "Retry - Not Available",
    "Final — Answering Machine",
    "Answering Machine Left Message",
]);

const SYSTEM_DISPOSITIONS = new Set([
    "Error",
]);

export function CallerIdPerformance({
    callerIds = [],
    userContext,
}) {

    const [callerIdResults, setCallerIdResults] = useState([]);
    const [layout, setLayout] = useState("grid");
    const [range, setRange] = useState("7");
    const [loading, setLoading] = useState(false);

    const [expandedRows, setExpandedRows] = useState({});

    function toggleRow(number) {
        setExpandedRows((prev) => ({
            ...prev,
            [number]: !prev[number],
        }));
    }

    const rangeDates = useMemo(() => {
        const end = new Date();
        const start = new Date();

        start.setDate(end.getDate() - Number(range));

        return {
            gte: start.toISOString(),
            lte: end.toISOString(),
        };
    }, [range]);

    const callerIdNumbers = useMemo(
        () =>
            callerIds
                .map((callerId) => callerId.number)
                .join(","),
        [callerIds]
    );

    useEffect(() => {
        if (!callerIds.length) {
            setCallerIdResults([]);
            return;
        }

        loadCallerIdPerformance();
    }, [callerIdNumbers, range]);

    async function loadCallerIdPerformance() {
        try {
            setLoading(true);

            const response = await client.graphql({
                query: esQuery,
                variables: {
                    model: 'activitylog-*',
                    action: '_search',
                    query: JSON.stringify(getCallerIdQuery())
                }
            });

            const responseData = JSON.parse(response.data.esQuery);
            console.log('Response Data: ', responseData);


            const buckets =
                responseData?.aggregations?.callerId?.buckets || [];

            // Build lookup map once instead of repeatedly .find()
            // Much faster if you end up with lots of caller IDs
            const bucketMap = new Map(
                buckets.map((bucket) => [bucket.key, bucket])
            );

            const results = callerIds.map((callerId) => {
                const bucket = bucketMap.get(callerId.number);

                const dispositionBuckets =
                    bucket?.disposition?.buckets || [];

                const totalCalls =
                    bucket?.doc_count ||
                    dispositionBuckets.reduce(
                        (sum, item) => sum + item.doc_count,
                        0
                    );

                // Flexible "connected" matching
                // lets you support multiple positive dispositions later
                const connectedCalls = dispositionBuckets
                    .filter((item) =>
                        CONNECTED_DISPOSITIONS.has(item.key)
                    )
                    .reduce(
                        (sum, item) => sum + item.doc_count,
                        0
                    );

                const contactCalls = dispositionBuckets
                    .filter((item) =>
                        CONTACT_DISPOSITIONS.has(item.key)
                    )
                    .reduce(
                        (sum, item) => sum + item.doc_count,
                        0
                    );

                const badNumberCalls = dispositionBuckets
                    .filter((item) =>
                        BAD_NUMBER_DISPOSITIONS.has(item.key)
                    )
                    .reduce(
                        (sum, item) => sum + item.doc_count,
                        0
                    );

                const retryCalls = dispositionBuckets
                    .filter((item) =>
                        RETRY_DISPOSITIONS.has(item.key)
                    )
                    .reduce(
                        (sum, item) => sum + item.doc_count,
                        0
                    );

                const errorCalls = dispositionBuckets
                    .filter((item) =>
                        SYSTEM_DISPOSITIONS.has(item.key)
                    )
                    .reduce(
                        (sum, item) => sum + item.doc_count,
                        0
                    );

                // True agent connect rate
                const connectionRate = totalCalls
                    ? Math.round(
                        (contactCalls / totalCalls) * 100
                    )
                    : 0;

                // Actual positive conversion rate
                const positiveRate = contactCalls
                    ? Math.round(
                        (connectedCalls / contactCalls) * 100
                    )
                    : 0;

                return {
                    ...callerId,
                    totalCalls,
                    connectedCalls,
                    contactCalls,
                    badNumberCalls,
                    retryCalls,
                    errorCalls,
                    positiveRate: `${positiveRate}%`,
                    connectionRate: `${connectionRate}%`,
                    dispositions: dispositionBuckets.map(
                        (disposition) => ({
                            value: disposition.doc_count,
                            label: disposition.key,
                            color:
                                DISPOSITION_COLORS[
                                disposition.key
                                ] || "#bdbdbd",
                        })
                    ),
                };
            });

            // Sort highest volume first
            results.sort(
                (a, b) => b.totalCalls - a.totalCalls
            );

            setCallerIdResults(results);

        } catch (error) {
            console.error(
                "Failed to load caller ID performance",
                error
            );
        } finally {
            setLoading(false);
        }
    }

    function getCallerIdQuery() {
        return {
            aggs: {
                callerId: {
                    terms: {
                        field: "outboundCallerId.keyword",
                        size: callerIds.length,
                    },
                    aggs: {
                        disposition: {
                            terms: {
                                field: "disposition.keyword",
                                size: 50,
                            },
                        },
                    },
                },
            },
            query: {
                bool: {
                    must: [
                        {
                            term: {
                                "tenant.keyword":
                                    userContext.tenantId,
                            },
                        },
                        {
                            terms: {
                                "outboundCallerId.keyword":
                                    callerIds.map(
                                        (callerId) =>
                                            callerId.number
                                    ),
                            },
                        },
                        {
                            match_phrase: {
                                type: "Dial",
                            },
                        },
                        {
                            range: {
                                createdAt: rangeDates,
                            },
                        },
                    ],
                    must_not: [
                        {
                            match_phrase: {
                                disposition:
                                    "Cadence Exhaustion - Retry Increment",
                            },
                        },
                    ],
                },
            },
            track_total_hits: false,
            size: 0,
        };
    }

    return (
        <Card variant="outlined">
            <CardHeader
                title="Caller ID Performance"
                subheader={`${callerIdResults.length} caller IDs`}
                action={
                    <Stack
                        direction="row"
                        spacing={1.5}
                        flexWrap="wrap"
                        justifyContent="flex-end"
                    >
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={range}
                            onChange={(_, value) => {
                                if (value) setRange(value);
                            }}
                        >
                            <ToggleButton value="1">
                                1 Day
                            </ToggleButton>

                            <ToggleButton value="7">
                                7 Days
                            </ToggleButton>

                            <ToggleButton value="30">
                                30 Days
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={layout}
                            onChange={(_, value) => {
                                if (value) setLayout(value);
                            }}
                        >
                            <ToggleButton value="list">
                                <ListOutlined fontSize="small" />
                            </ToggleButton>

                            <ToggleButton value="grid">
                                <GridViewOutlined fontSize="small" />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                }
            />

            <Divider />

            <CardContent>
                {loading ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        py={6}
                    >
                        <CircularProgress size={28} />
                    </Box>
                ) : layout === "grid" ? (
                    <Box
                        display="grid"
                        gap={2}
                        gridTemplateColumns={{
                            xs: "1fr",
                            sm: "repeat(2, minmax(0, 1fr))",
                            md: "repeat(3, minmax(0, 1fr))",
                            lg: "repeat(4, minmax(0, 1fr))",
                        }}
                    >
                        {callerIdResults.map((callerId) => (
                            <Card
                                key={callerId.number}
                                variant="outlined"
                                sx={{
                                    borderRadius: 3,
                                    transition:
                                        "all .2s ease",
                                    overflow: "hidden",
                                    "&:hover": {
                                        boxShadow: 2,
                                    },
                                }}
                            >
                                <CardContent
                                    sx={{
                                        p: 2,
                                        "&:last-child": {
                                            pb: 2,
                                        },
                                    }}
                                >
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                        gap={1.5}
                                    >
                                        {callerId.totalCalls > 0 && <PieChart
                                            height={95}
                                            width={95}
                                            margin={{
                                                top: 0,
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                            }}
                                            slotProps={{
                                                legend: {
                                                    hidden: true,
                                                },
                                            }}
                                            series={[
                                                {
                                                    data:
                                                        callerId.dispositions,
                                                    innerRadius: 24,
                                                    outerRadius: 40,
                                                    paddingAngle: 2,
                                                    cornerRadius: 4,
                                                    cx: 47,
                                                    cy: 47,
                                                },
                                            ]}
                                        />}
                                        {callerId.totalCalls < 1 &&
                                            <Typography variant="body2">
                                                No calls
                                            </Typography>
                                        }

                                        <Box textAlign="center">
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={700}
                                                noWrap
                                            >
                                                {callerId.name ||
                                                    callerId.number}
                                            </Typography>

                                            {callerId.name && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {callerId.number}
                                                </Typography>
                                            )}
                                        </Box>

                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            flexWrap="wrap"
                                            justifyContent="center"
                                        >
                                            <Chip
                                                size="small"
                                                label={`${callerId.totalCalls} Calls`}
                                            />

                                            <Chip
                                                size="small"
                                                color="success"
                                                label={`${callerId.connectionRate} Connected`}
                                            />
                                        </Stack>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell width={48} />

                                    <TableCell
                                        sx={{
                                            fontWeight: 700,
                                        }}
                                    >
                                        Caller ID
                                    </TableCell>

                                    <TableCell
                                        sx={{
                                            fontWeight: 700,
                                        }}
                                    >
                                        Carrier
                                    </TableCell>

                                    <TableCell
                                        align="right"
                                        sx={{
                                            fontWeight: 700,
                                        }}
                                    >
                                        Calls
                                    </TableCell>

                                    <TableCell
                                        align="right"
                                        sx={{
                                            fontWeight: 700,
                                        }}
                                    >
                                        Connected
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {!callerIdResults.length ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={5}
                                            sx={{
                                                py: 6,
                                                borderBottom: 0,
                                            }}
                                        >
                                            <Box textAlign="center">
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    No caller ID activity found
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    callerIdResults.map((callerId) => {
                                        const isExpanded =
                                            expandedRows[callerId.number];

                                        return (
                                            <>
                                                <TableRow
                                                    key={callerId.number}
                                                    hover
                                                >
                                                    <TableCell>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                toggleRow(
                                                                    callerId.number
                                                                )
                                                            }
                                                        >
                                                            {isExpanded ? (
                                                                <KeyboardArrowUpOutlined fontSize="small" />
                                                            ) : (
                                                                <KeyboardArrowDownOutlined fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                    </TableCell>

                                                    <TableCell>
                                                        <Box>
                                                            <Typography
                                                                variant="subtitle2"
                                                                fontWeight={600}
                                                            >
                                                                {callerId.name ||
                                                                    callerId.number}
                                                            </Typography>

                                                            {callerId.name && (
                                                                <Typography
                                                                    variant="body2"
                                                                    color="text.secondary"
                                                                >
                                                                    {callerId.number}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </TableCell>

                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {callerId.carrier ||
                                                                "-"}
                                                        </Typography>
                                                    </TableCell>

                                                    <TableCell align="right">
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={600}
                                                        >
                                                            {
                                                                callerId.totalCalls
                                                            }
                                                        </Typography>
                                                    </TableCell>

                                                    <TableCell align="right">
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={700}
                                                            color={
                                                                Number.parseInt(
                                                                    callerId.connectionRate
                                                                ) >= 50
                                                                    ? "success.main"
                                                                    : "warning.main"
                                                            }
                                                        >
                                                            {
                                                                callerId.connectionRate
                                                            }
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>

                                                <TableRow>
                                                    <TableCell
                                                        colSpan={5}
                                                        sx={{
                                                            py: 0,
                                                            borderBottom:
                                                                isExpanded
                                                                    ? undefined
                                                                    : 0,
                                                        }}
                                                    >
                                                        <Collapse
                                                            in={isExpanded}
                                                            timeout="auto"
                                                            unmountOnExit
                                                        >
                                                            <Box
                                                                sx={{
                                                                    px: 2,
                                                                    py: 2,
                                                                    backgroundColor:
                                                                        "background.default",
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    fontWeight={
                                                                        700
                                                                    }
                                                                    mb={1.5}
                                                                >
                                                                    Dispositions
                                                                </Typography>

                                                                <Table
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor:
                                                                            "background.paper",
                                                                        borderRadius: 2,
                                                                        overflow:
                                                                            "hidden",
                                                                    }}
                                                                >
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell>
                                                                                Disposition
                                                                            </TableCell>

                                                                            <TableCell align="right">
                                                                                Calls
                                                                            </TableCell>

                                                                            <TableCell align="right">
                                                                                Percentage
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    </TableHead>

                                                                    <TableBody>
                                                                        {callerId.dispositions
                                                                            ?.sort(
                                                                                (
                                                                                    a,
                                                                                    b
                                                                                ) =>
                                                                                    b.value -
                                                                                    a.value
                                                                            )
                                                                            .map(
                                                                                (
                                                                                    disposition
                                                                                ) => {
                                                                                    const percentage =
                                                                                        callerId.totalCalls
                                                                                            ? Math.round(
                                                                                                (
                                                                                                    disposition.value /
                                                                                                    callerId.totalCalls
                                                                                                ) *
                                                                                                100
                                                                                            )
                                                                                            : 0;

                                                                                    return (
                                                                                        <TableRow
                                                                                            key={
                                                                                                disposition.label
                                                                                            }
                                                                                        >
                                                                                            <TableCell>
                                                                                                <Stack
                                                                                                    direction="row"
                                                                                                    spacing={
                                                                                                        1
                                                                                                    }
                                                                                                    alignItems="center"
                                                                                                >
                                                                                                    <Box
                                                                                                        sx={{
                                                                                                            width: 10,
                                                                                                            height: 10,
                                                                                                            borderRadius:
                                                                                                                "50%",
                                                                                                            backgroundColor:
                                                                                                                disposition.color,
                                                                                                        }}
                                                                                                    />

                                                                                                    <Typography variant="body2">
                                                                                                        {
                                                                                                            disposition.label
                                                                                                        }
                                                                                                    </Typography>
                                                                                                </Stack>
                                                                                            </TableCell>

                                                                                            <TableCell align="right">
                                                                                                <Typography variant="body2">
                                                                                                    {
                                                                                                        disposition.value
                                                                                                    }
                                                                                                </Typography>
                                                                                            </TableCell>

                                                                                            <TableCell align="right">
                                                                                                <Typography
                                                                                                    variant="body2"
                                                                                                    color="text.secondary"
                                                                                                >
                                                                                                    {
                                                                                                        percentage
                                                                                                    }

                                                                                                    %
                                                                                                </Typography>
                                                                                            </TableCell>
                                                                                        </TableRow>
                                                                                    );
                                                                                }
                                                                            )}
                                                                    </TableBody>
                                                                </Table>
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    );
}